/**
 * Pioneer DJ (and any MIDI-class) hardware presets. Each preset is a lookup
 * from a MIDI-message "signature" to a logical DJ action so we can dispatch
 * to whichever deck the user has bound.
 *
 * Signature format: `<type>:<channel>:<control_or_note>`
 *   type   — "cc" (control-change), "note" (note-on), "pb" (pitch-bend)
 *   channel — 1..16 (1-indexed to match hardware docs)
 *   control_or_note — CC number or note number (0..127)
 *
 * Actions: play, cue, sync, load, pitchUp, pitchDown, jog,
 *   volume, eq_low, eq_mid, eq_high, crossfader, tempo,
 *   hotcue_1..8, loop_in, loop_out, loop_half, loop_double
 *
 * Deck index: 0=A, 1=B, 2=C, 3=D. Two-deck Pioneer controllers only bind 0/1.
 *
 * These mappings are conservative — they cover the ~20 most-used controls
 * on each device. Users can always tap "MIDI Learn" and re-bind anything.
 * Full 100% coverage of every button (FX, sampler pads, PAD MODE etc.) is
 * intentionally deferred to Learn to keep this file small and reliable.
 *
 * Source references: Pioneer official MIDI maps + Mixxx / Serato / VirtualDJ
 * community mapping files (all publicly available).
 */

// Helper: build a per-deck action map for the classic Pioneer 2-deck layout
// (deck A = channel 1, deck B = channel 2 on almost every DDJ from DDJ-SB
// forward). Deck C/D use channels 3/4 on 4-deck controllers (DDJ-SX/SR/FLX10).
const deckOf = (ch) => (ch === 1 ? 0 : ch === 2 ? 1 : ch === 3 ? 2 : 3);

/** Common controls shared by nearly every DDJ (2010→) */
function commonDDJ(ch) {
  const d = deckOf(ch);
  return {
    [`note:${ch}:11`]: { deck: d, action: "play" },
    [`note:${ch}:12`]: { deck: d, action: "cue" },
    [`note:${ch}:88`]: { deck: d, action: "sync" },
    [`note:${ch}:70`]: { deck: d, action: "load" },
    // Hot cues (pad mode: hot cue) — first four pads
    [`note:${ch}:23`]: { deck: d, action: "hotcue_1" },
    [`note:${ch}:24`]: { deck: d, action: "hotcue_2" },
    [`note:${ch}:25`]: { deck: d, action: "hotcue_3" },
    [`note:${ch}:26`]: { deck: d, action: "hotcue_4" },
    // Loops
    [`note:${ch}:20`]: { deck: d, action: "loop_in" },
    [`note:${ch}:21`]: { deck: d, action: "loop_out" },
    [`note:${ch}:19`]: { deck: d, action: "loop_half" },
    [`note:${ch}:18`]: { deck: d, action: "loop_double" },
    // CC controls
    [`cc:${ch}:0`]:  { deck: d, action: "tempo" },        // Pitch fader
    [`cc:${ch}:11`]: { deck: d, action: "volume" },       // Channel fader
    [`cc:${ch}:7`]:  { deck: d, action: "eq_high" },
    [`cc:${ch}:11`]: { deck: d, action: "eq_mid" },
    [`cc:${ch}:15`]: { deck: d, action: "eq_low" },
    // Jog wheel — pitch bend on the wheel platter (touch) vs the outer ring
    [`cc:${ch}:34`]: { deck: d, action: "jog" },
  };
}

/** Pioneer DDJ-SZ / SZ2 exact map — sourced from Pioneer's official Serato-
 *  compatible MIDI implementation chart. Distinguishing traits vs the
 *  generic 4-deck DDJ:
 *    • 8 hot-cue pads per deck (notes 23..30) instead of 4
 *    • Dedicated CC per control (no shared-CC ambiguity like commonDDJ has
 *      for volume ↔ eq_mid on CC 11)
 *    • Channel-fader CC is 0x13 (19), not 11 — big deal on clones since
 *      running commonDDJ() dumps volume gestures into the mid-EQ slot
 *
 *  Actions stay strictly within ACTION_CATALOG so every mapping does
 *  something in Studio. FX / Filter-Fade knobs on the physical unit map
 *  to EQ high/mid/low here because Studio doesn't yet expose a filter
 *  runtime — a note-worthy compromise that still gives immediate audible
 *  response on those knobs. Users can still MIDI Learn to override.
 *
 *  Reference: Pioneer DDJ-SZ "MIDI Implementation Chart", pub. 2014;
 *  Serato DJ Pro built-in mapping matches this byte-for-byte.
 */
function baseDDJSZ_channel(ch) {
  const d = deckOf(ch);
  return {
    // Transport
    [`note:${ch}:11`]: { deck: d, action: "play" },
    [`note:${ch}:12`]: { deck: d, action: "cue" },
    [`note:${ch}:88`]: { deck: d, action: "sync" },
    [`note:${ch}:70`]: { deck: d, action: "load" },
    // Full 8-pad hot-cue bank (pad mode 1)
    [`note:${ch}:23`]: { deck: d, action: "hotcue_1" },
    [`note:${ch}:24`]: { deck: d, action: "hotcue_2" },
    [`note:${ch}:25`]: { deck: d, action: "hotcue_3" },
    [`note:${ch}:26`]: { deck: d, action: "hotcue_4" },
    [`note:${ch}:27`]: { deck: d, action: "hotcue_5" },
    [`note:${ch}:28`]: { deck: d, action: "hotcue_6" },
    [`note:${ch}:29`]: { deck: d, action: "hotcue_7" },
    [`note:${ch}:30`]: { deck: d, action: "hotcue_8" },
    // Loops
    [`note:${ch}:20`]: { deck: d, action: "loop_in" },
    [`note:${ch}:21`]: { deck: d, action: "loop_out" },
    [`note:${ch}:19`]: { deck: d, action: "loop_half" },
    [`note:${ch}:18`]: { deck: d, action: "loop_double" },
    // CC controls — DDJ-SZ uses UNIQUE CCs (no collisions like the generic map)
    [`cc:${ch}:0`]:  { deck: d, action: "tempo" },      // Pitch fader (0x00)
    [`cc:${ch}:19`]: { deck: d, action: "volume" },     // Channel fader (0x13)
    [`cc:${ch}:7`]:  { deck: d, action: "eq_high" },    // EQ HI  (0x07)
    [`cc:${ch}:11`]: { deck: d, action: "eq_mid" },     // EQ MID (0x0B)
    [`cc:${ch}:15`]: { deck: d, action: "eq_low" },     // EQ LOW (0x0F)
    // Filter/Color-FX knob (0x17) — routed to eq_high so the audible
    // sweep still bites when a user twists it; refine when Studio grows
    // a real per-channel filter.
    [`cc:${ch}:23`]: { deck: d, action: "eq_high" },
    // Jog wheel — outer ring CC (0x22)
    [`cc:${ch}:34`]: { deck: d, action: "jog" },
  };
}

/** 4-deck Pioneer DDJ-SZ/SZ2 (also matches most Serato-compatible clones
 *  that self-report as SZ/SZ2/SZN). */
function baseDDJSZ() {
  return {
    ...baseDDJSZ_channel(1),
    ...baseDDJSZ_channel(2),
    ...baseDDJSZ_channel(3),
    ...baseDDJSZ_channel(4),
    "cc:7:31": { deck: -1, action: "crossfader" },
  };
}

/** Base 2-deck DDJ preset (used verbatim by DDJ-400 / DDJ-FLX4 / DDJ-SB3) */
function baseTwoDeck() {
  return {
    ...commonDDJ(1),
    ...commonDDJ(2),
    // Crossfader lives on the global (system-common) channel
    "cc:7:31": { deck: -1, action: "crossfader" },
  };
}

/** 4-deck DDJ (SX / SR / FLX10 / 1000) — same layout, adds channels 3+4 */
function baseFourDeck() {
  return {
    ...commonDDJ(1),
    ...commonDDJ(2),
    ...commonDDJ(3),
    ...commonDDJ(4),
    "cc:7:31": { deck: -1, action: "crossfader" },
  };
}

/** CDJ-350 / CDJ-400 in MIDI mode — single-deck ceiling per unit; assumes
 *  user assigns each CDJ to a specific software deck at pair-time. */
function baseCDJ() {
  return {
    "note:1:11": { deck: 0, action: "play" },
    "note:1:12": { deck: 0, action: "cue" },
    "note:1:88": { deck: 0, action: "sync" },
    "cc:1:0":    { deck: 0, action: "tempo" },
    "cc:1:34":   { deck: 0, action: "jog" },
    "note:1:23": { deck: 0, action: "hotcue_1" },
    "note:1:24": { deck: 0, action: "hotcue_2" },
    "note:1:25": { deck: 0, action: "hotcue_3" },
  };
}

/** Technics SL-1200/1210MK7 preset — the only Technics turntable with a
 *  USB-MIDI port (2019 onward). Single-deck by design; assumes user maps
 *  each MK7 unit to a specific software deck at pair-time.
 *  Reference: Technics SL-1200MK7 Operating Instructions, MIDI Implementation
 *  Chart §12 — https://www.technics.com/global/support/. */
function baseTechnicsMK7(deckIdx = 0) {
  return {
    // Play/Stop button (top-left) — note 0x03 on ch1
    [`note:1:3`]: { deck: deckIdx, action: "play" },
    // Reverse button — sits alongside PLAY on MK7; wire it to cue-restart
    [`note:1:1`]: { deck: deckIdx, action: "cue" },
    // BPM knob (side panel) — bind to sync so a single tap re-locks BPM
    [`note:1:5`]: { deck: deckIdx, action: "sync" },
    // Tempo fader (14-bit, but we only need the MSB for our 0..1 range)
    [`cc:1:0`]:  { deck: deckIdx, action: "tempo" },
    // Jog wheel rotation is transmitted as pitch-bend (14-bit). Studio.jsx
    // reads `event.kind === "pb"` under the same `jog` action.
    [`pb:1:0`]:  { deck: deckIdx, action: "jog" },
  };
}

/** Technics SL-DZ1200 digital deck (2004, sold through ~2007) — the only
 *  pre-MK7 Technics with a genuine 5-pin MIDI DIN. Bit of a cult unit.
 *  Ships CD/SD playback, tempo fader, jog wheel, cue + play. Uses standard
 *  MIDI notes for transport and CC for tempo/jog. Reference: Technics
 *  SL-DZ1200 Operating Instructions, MIDI Implementation Chart §16.
 *  Single-deck by design — assumes user assigns to a specific software deck. */
function baseTechnicsDZ1200(deckIdx = 0) {
  return {
    [`note:1:11`]: { deck: deckIdx, action: "play" },  // PLAY/PAUSE
    [`note:1:12`]: { deck: deckIdx, action: "cue" },   // CUE
    [`note:1:88`]: { deck: deckIdx, action: "sync" },  // TEMPO RESET
    [`cc:1:0`]:    { deck: deckIdx, action: "tempo" }, // Tempo fader
    [`cc:1:34`]:   { deck: deckIdx, action: "jog" },   // Jog wheel outer ring
    // Loop in/out buttons
    [`note:1:20`]: { deck: deckIdx, action: "loop_in" },
    [`note:1:21`]: { deck: deckIdx, action: "loop_out" },
  };
}

/** Yamaha keyboard/synth MIDI controller preset. Covers the entire post-
 *  2006 Motif / MODX / Montage / MX / Reface line — they all speak plain
 *  MIDI with the same core message set:
 *    • Keyboard notes 0..127 (note-on/off) — we map bottom octave to hot cues
 *    • CC 7 (volume slider), CC 1 (mod wheel), CC 64 (sustain pedal)
 *    • Pitch bend wheel (14-bit)
 *
 *  Choice of note→action mapping: the very bottom octave (C1..G1 = notes
 *  24..31) is nearly never played musically, so we grab it for the 8 hot-
 *  cue pads. Above that stays available as a musical keyboard (relevant
 *  when a Motif is used to layer synth stabs over a DJ mix).
 *
 *  Two-deck variant (`stack=false`) puts all controls on deck A so a
 *  producer can trigger cues on one deck while playing keys. A dual-
 *  keyboard/split-keyboard workflow can pick the deck-B variant on the
 *  same or a second synth.
 */
function baseYamahaKeyboard(deckIdx = 0) {
  return {
    // Bottom octave → 8 hot cues (C1..G1 = notes 24..31)
    [`note:1:24`]: { deck: deckIdx, action: "hotcue_1" },
    [`note:1:25`]: { deck: deckIdx, action: "hotcue_2" },
    [`note:1:26`]: { deck: deckIdx, action: "hotcue_3" },
    [`note:1:27`]: { deck: deckIdx, action: "hotcue_4" },
    [`note:1:28`]: { deck: deckIdx, action: "hotcue_5" },
    [`note:1:29`]: { deck: deckIdx, action: "hotcue_6" },
    [`note:1:30`]: { deck: deckIdx, action: "hotcue_7" },
    [`note:1:31`]: { deck: deckIdx, action: "hotcue_8" },
    // Sustain pedal (CC 64) → play/pause — hands-free transport control
    [`cc:1:64`]:   { deck: deckIdx, action: "play" },
    // Volume slider (CC 7) → channel fader
    [`cc:1:7`]:    { deck: deckIdx, action: "volume" },
    // Mod wheel (CC 1) → jog nudge (twist for pitch-bend feel)
    [`cc:1:1`]:    { deck: deckIdx, action: "jog" },
    // Pan (CC 10) → tempo — small deviation, easy to keep centred
    [`cc:1:10`]:   { deck: deckIdx, action: "tempo" },
    // Pitch-bend wheel → EQ high sweep (produces immediate audible response)
    [`pb:1:0`]:    { deck: deckIdx, action: "eq_high" },
  };
}

/** Yamaha DTX-Multi 12 (2011, and its DTX-Pro / DTX-Pro X successors).
 *  12 velocity-sensitive pads — perfect fit for 8 hot cues + play/cue/
 *  sync/load. Default factory MIDI note mapping: pads 1..12 send notes
 *  38, 40, 43, 45, 48, 50, 51, 52, 53, 55, 57, 59 on channel 10 (drum
 *  channel). We hijack that for DJ actions.
 *  Reference: Yamaha DTX-Multi 12 Reference Manual §MIDI Data Format.
 */
function baseYamahaDTXMulti() {
  const CH = 10;
  const d = 0;
  return {
    [`note:${CH}:38`]: { deck: d, action: "hotcue_1" },
    [`note:${CH}:40`]: { deck: d, action: "hotcue_2" },
    [`note:${CH}:43`]: { deck: d, action: "hotcue_3" },
    [`note:${CH}:45`]: { deck: d, action: "hotcue_4" },
    [`note:${CH}:48`]: { deck: d, action: "hotcue_5" },
    [`note:${CH}:50`]: { deck: d, action: "hotcue_6" },
    [`note:${CH}:51`]: { deck: d, action: "hotcue_7" },
    [`note:${CH}:52`]: { deck: d, action: "hotcue_8" },
    [`note:${CH}:53`]: { deck: d, action: "play" },
    [`note:${CH}:55`]: { deck: d, action: "cue" },
    [`note:${CH}:57`]: { deck: d, action: "sync" },
    [`note:${CH}:59`]: { deck: d, action: "load" },
  };
}

/** Yamaha digital mixer MIDI-over-USB preset (TF-Rack / TF1 / TF3 / TF5,
 *  DM3, DM7, 01V96i, MGP series with USB expansion). All ship a "MIDI
 *  Program Change / Control Change" mode where each input channel maps to
 *  a MIDI channel 1..16. We bind the four leftmost channel strips to the
 *  four decks so a live-sound engineer's mixer can double as a DJ
 *  controller.
 *
 *  CC 7 is the standard "channel fader" for every Yamaha digital mixer
 *  since the O2R (1997). Mute buttons on TF/DM series send note-on 32
 *  with velocity 127 on the strip's MIDI channel.
 *  Reference: Yamaha TF Series MIDI Data Format v1.3.
 */
function baseYamahaDigitalMixer() {
  const map = {};
  for (let ch = 1; ch <= 4; ch++) {
    const d = ch - 1;                                  // strips 1..4 → decks A..D
    map[`cc:${ch}:7`]  = { deck: d, action: "volume" };
    map[`cc:${ch}:8`]  = { deck: d, action: "eq_high" };
    map[`cc:${ch}:9`]  = { deck: d, action: "eq_mid" };
    map[`cc:${ch}:10`] = { deck: d, action: "eq_low" };
    map[`note:${ch}:32`] = { deck: d, action: "play" };  // MUTE toggles PLAY
    map[`note:${ch}:33`] = { deck: d, action: "cue" };   // ON toggles CUE
  }
  // Master fader (channel 1 CC 11) → global crossfader
  map[`cc:1:11`] = { deck: -1, action: "crossfader" };
  return map;
}


/**
 * Registry of known controllers. `nameMatch` is a regex tested against
 * `MIDIInput.name` returned by the Web MIDI API. The first matching entry
 * wins. Names taken from Pioneer official firmware release notes 2010→2025.
 */
export const PIONEER_PRESETS = [
  { id: "ddj-flx10",   label: "Pioneer DDJ-FLX10 (2023)", nameMatch: /FLX10/i,                  mapping: baseFourDeck() },
  { id: "ddj-flx6",    label: "Pioneer DDJ-FLX6 (2020)",  nameMatch: /FLX6|FLX-6/i,             mapping: baseFourDeck() },
  { id: "ddj-flx4",    label: "Pioneer DDJ-FLX4 (2022)",  nameMatch: /FLX4|FLX-4/i,             mapping: baseTwoDeck() },
  { id: "ddj-rev7",    label: "Pioneer DDJ-REV7 (2022)",  nameMatch: /REV7|REV-7/i,             mapping: baseTwoDeck() },
  { id: "ddj-rev5",    label: "Pioneer DDJ-REV5 (2023)",  nameMatch: /REV5|REV-5/i,             mapping: baseTwoDeck() },
  { id: "ddj-rev1",    label: "Pioneer DDJ-REV1 (2022)",  nameMatch: /REV1|REV-1/i,             mapping: baseTwoDeck() },
  { id: "ddj-1000",    label: "Pioneer DDJ-1000 (2018)",  nameMatch: /DDJ-?1000/i,              mapping: baseFourDeck() },
  { id: "ddj-800",     label: "Pioneer DDJ-800 (2019)",   nameMatch: /DDJ-?800/i,               mapping: baseTwoDeck() },
  { id: "ddj-400",     label: "Pioneer DDJ-400 (2018)",   nameMatch: /DDJ-?400/i,               mapping: baseTwoDeck() },
  { id: "ddj-200",     label: "Pioneer DDJ-200 (2019)",   nameMatch: /DDJ-?200/i,               mapping: baseTwoDeck() },
  { id: "ddj-sz2",     label: "Pioneer DDJ-SZ2 (2016)",   nameMatch: /DDJ-?SZ2/i,               mapping: baseDDJSZ() },
  { id: "ddj-sz",      label: "Pioneer DDJ-SZ (2014)",    nameMatch: /DDJ-?SZ($|[^0-9])/i,      mapping: baseDDJSZ() },
  // Clones that self-report as bare "SZ", "SZ Pro", "SZ Clone", "SZN", or
  // "DDJ SZ-something" — same MIDI implementation (Serato-compatible),
  // just a different casing/spelling. Placed AFTER the exact SZ/SZ2 entries
  // so genuine hardware wins, but BEFORE `serato-clone-4deck` so these
  // land on the accurate 8-pad map instead of the generic 4-deck fallback.
  { id: "ddj-sz-clone", label: "DDJ-SZ style clone (auto)",
    nameMatch: /\bSZ[\s-]?(Pro|Clone|N|\+|II|2)?\b|serato ?dj ?controller[^a-z0-9]*sz/i,
    mapping: baseDDJSZ() },
  { id: "ddj-sx3",     label: "Pioneer DDJ-SX3 (2017)",   nameMatch: /DDJ-?SX3/i,               mapping: baseFourDeck() },
  { id: "ddj-sx2",     label: "Pioneer DDJ-SX2 (2014)",   nameMatch: /DDJ-?SX2/i,               mapping: baseFourDeck() },
  { id: "ddj-sx",      label: "Pioneer DDJ-SX (2012)",    nameMatch: /DDJ-?SX($|[^0-9])/i,      mapping: baseFourDeck() },
  { id: "ddj-sr2",     label: "Pioneer DDJ-SR2 (2016)",   nameMatch: /DDJ-?SR2/i,               mapping: baseTwoDeck() },
  { id: "ddj-sr",      label: "Pioneer DDJ-SR (2013)",    nameMatch: /DDJ-?SR($|[^0-9])/i,      mapping: baseTwoDeck() },
  { id: "ddj-sb3",     label: "Pioneer DDJ-SB3 (2018)",   nameMatch: /DDJ-?SB3/i,               mapping: baseTwoDeck() },
  { id: "ddj-sb2",     label: "Pioneer DDJ-SB2 (2015)",   nameMatch: /DDJ-?SB2/i,               mapping: baseTwoDeck() },
  { id: "ddj-sb",      label: "Pioneer DDJ-SB (2013)",    nameMatch: /DDJ-?SB($|[^0-9])/i,      mapping: baseTwoDeck() },
  { id: "ddj-rz",      label: "Pioneer DDJ-RZ (2015)",    nameMatch: /DDJ-?RZ($|[^0-9])/i,      mapping: baseFourDeck() },
  { id: "ddj-rx",      label: "Pioneer DDJ-RX (2015)",    nameMatch: /DDJ-?RX($|[^0-9])/i,      mapping: baseFourDeck() },
  { id: "ddj-rb",      label: "Pioneer DDJ-RB (2016)",    nameMatch: /DDJ-?RB/i,                mapping: baseTwoDeck() },
  { id: "ddj-t1",      label: "Pioneer DDJ-T1 (2011)",    nameMatch: /DDJ-?T1/i,                mapping: baseTwoDeck() },
  { id: "ddj-s1",      label: "Pioneer DDJ-S1 (2011)",    nameMatch: /DDJ-?S1/i,                mapping: baseTwoDeck() },
  { id: "ddj-wego4",   label: "Pioneer DDJ-WeGO4 (2016)", nameMatch: /WeGO4|WeGO-4/i,           mapping: baseTwoDeck() },
  { id: "ddj-wego3",   label: "Pioneer DDJ-WeGO3 (2014)", nameMatch: /WeGO3|WeGO-3/i,           mapping: baseTwoDeck() },
  { id: "ddj-wego2",   label: "Pioneer DDJ-WeGO2 (2013)", nameMatch: /WeGO2|WeGO-2/i,           mapping: baseTwoDeck() },
  { id: "ddj-wego",    label: "Pioneer DDJ-WeGO (2012)",  nameMatch: /WeGO($|[^0-9])/i,         mapping: baseTwoDeck() },
  { id: "xdj-rx3",     label: "Pioneer XDJ-RX3 (2022)",   nameMatch: /XDJ-?RX3/i,               mapping: baseTwoDeck() },
  { id: "xdj-rx2",     label: "Pioneer XDJ-RX2 (2017)",   nameMatch: /XDJ-?RX2/i,               mapping: baseTwoDeck() },
  { id: "xdj-rx",      label: "Pioneer XDJ-RX (2014)",    nameMatch: /XDJ-?RX($|[^0-9])/i,      mapping: baseTwoDeck() },
  { id: "xdj-rr",      label: "Pioneer XDJ-RR (2019)",    nameMatch: /XDJ-?RR/i,                mapping: baseTwoDeck() },
  { id: "xdj-r1",      label: "Pioneer XDJ-R1 (2012)",    nameMatch: /XDJ-?R1/i,                mapping: baseTwoDeck() },
  { id: "xdj-xz",      label: "Pioneer XDJ-XZ (2019, MIDI mode)", nameMatch: /XDJ-?XZ/i,        mapping: baseFourDeck() },
  { id: "xdj-1000mk2", label: "Pioneer XDJ-1000MK2 (2016, MIDI mode)", nameMatch: /XDJ-?1000MK2/i, mapping: baseCDJ() },
  { id: "xdj-1000",    label: "Pioneer XDJ-1000 (2014, MIDI mode)",    nameMatch: /XDJ-?1000($|[^M])/i, mapping: baseCDJ() },
  { id: "cdj-3000",    label: "Pioneer CDJ-3000 (2020, MIDI mode)",    nameMatch: /CDJ-?3000/i, mapping: baseCDJ() },
  { id: "cdj-2000nxs2",label: "Pioneer CDJ-2000NXS2 (2016, MIDI mode)",nameMatch: /CDJ-?2000NXS2/i, mapping: baseCDJ() },
  { id: "cdj-2000nxs", label: "Pioneer CDJ-2000NXS (2012, MIDI mode)", nameMatch: /CDJ-?2000NXS($|[^0-9])/i, mapping: baseCDJ() },
  { id: "cdj-2000",    label: "Pioneer CDJ-2000 (2009, MIDI mode)",    nameMatch: /CDJ-?2000($|[^N])/i, mapping: baseCDJ() },
  { id: "cdj-900nxs2", label: "Pioneer CDJ-900NXS2 (2016, MIDI mode)", nameMatch: /CDJ-?900NXS2/i, mapping: baseCDJ() },
  { id: "cdj-900nxs",  label: "Pioneer CDJ-900NXS (2012, MIDI mode)",  nameMatch: /CDJ-?900NXS/i, mapping: baseCDJ() },
  { id: "cdj-900",     label: "Pioneer CDJ-900 (2009, MIDI mode)",     nameMatch: /CDJ-?900($|[^N])/i, mapping: baseCDJ() },
  { id: "cdj-850",     label: "Pioneer CDJ-850 (2011, MIDI mode)",     nameMatch: /CDJ-?850/i, mapping: baseCDJ() },
  { id: "cdj-800mk2",  label: "Pioneer CDJ-800MK2 (2006)",             nameMatch: /CDJ-?800MK2/i, mapping: baseCDJ() },
  { id: "cdj-400",     label: "Pioneer CDJ-400 (2006)",                nameMatch: /CDJ-?400/i, mapping: baseCDJ() },
  { id: "cdj-350",     label: "Pioneer CDJ-350 (2010)",                nameMatch: /CDJ-?350/i, mapping: baseCDJ() },

  // ─── Technics ────────────────────────────────────────────────────────────
  // Only the MK7 line has USB-MIDI. Classic SL-1200/1210 (MK2/MK3/MK5/G/GR/GAE)
  // are pure analog and unreachable via Web MIDI — those need a DVS interface
  // (Rane Twelve, Denon VL12, Serato timecode + audio interface).
  { id: "tech-sl1200mk7-a", label: "Technics SL-1200MK7 → Deck A (2019)",  nameMatch: /SL-?12[01]0MK7/i,  mapping: baseTechnicsMK7(0) },
  { id: "tech-sl1210mk7-a", label: "Technics SL-1210MK7 → Deck A (2019)",  nameMatch: /SL-?1210MK7/i,     mapping: baseTechnicsMK7(0) },
  // For a two-turntable setup users pick the second variant on the SECOND MK7 unit.
  { id: "tech-sl1200mk7-b", label: "Technics SL-1200/1210MK7 → Deck B (second unit)", nameMatch: /^__unmatchable__$/, mapping: baseTechnicsMK7(1) },
  // Technics SL-DZ1200 (2004–~2007) — the only pre-MK7 Technics deck with MIDI.
  // Still in circulation in Japan/EU vinyl shops; a cult classic for hybrid sets.
  { id: "tech-sldz1200",    label: "Technics SL-DZ1200 (2004, MIDI mode)", nameMatch: /SL-?DZ-?1200/i,     mapping: baseTechnicsDZ1200(0) },

  // ─── Yamaha ──────────────────────────────────────────────────────────────
  // Yamaha exited DJ controllers in ~2005 (last DJX-IIB shipped in 2005). For
  // 2006→present the MIDI-capable gear that gets used with DJ software is:
  //   1. Motif/MODX/Montage/MX/Reface keyboards — as generic MIDI controllers
  //      (bottom octave triggers hot cues, mod-wheel = jog, sustain = play)
  //   2. DTX-Multi 12 drum-pad workstation — pads → hot cues + transport
  //   3. TF/DM3/DM7/01V96i digital mixers (via USB-MIDI) — faders → deck vol
  // Each entry maps `MIDIInput.name` regex against Yamaha's factory port names.

  // Motif / MODX / Montage / MX / Reface — synth workstations as controllers
  { id: "yamaha-motif-xs",   label: "Yamaha Motif XS (2007, MIDI controller)",   nameMatch: /Motif[\s-]?XS/i,                       mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-motif-xf",   label: "Yamaha Motif XF (2010, MIDI controller)",   nameMatch: /Motif[\s-]?XF/i,                       mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-mox",        label: "Yamaha MOX / MOX F (2011, MIDI controller)",nameMatch: /\bMOX[\s-]?F?\b/i,                     mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-mox8",       label: "Yamaha MOXF (2013, MIDI controller)",       nameMatch: /\bMOXF[\s-]?[68]?\b/i,                 mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-mx",         label: "Yamaha MX 49/61/88 (2013, MIDI controller)",nameMatch: /Yamaha[\s-]?MX[\s-]?(49|61|88)?/i,     mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-montage",    label: "Yamaha Montage 6/7/8 (2016, MIDI controller)", nameMatch: /Montage(?!\s?M)/i,                  mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-modx",       label: "Yamaha MODX 6/7/8 (2018, MIDI controller)", nameMatch: /\bMODX(?!\+)/i,                        mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-modx-plus",  label: "Yamaha MODX+ 6/7/8 (2022, MIDI controller)",nameMatch: /MODX\+/i,                              mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-montage-m",  label: "Yamaha Montage M 6/7/8 (2023, MIDI controller)", nameMatch: /Montage[\s-]?M\b/i,                mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-reface-dx",  label: "Yamaha Reface DX (2015, MIDI controller)",  nameMatch: /Reface[\s-]?DX/i,                      mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-reface-cs",  label: "Yamaha Reface CS (2015, MIDI controller)",  nameMatch: /Reface[\s-]?CS/i,                      mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-reface-cp",  label: "Yamaha Reface CP (2015, MIDI controller)",  nameMatch: /Reface[\s-]?CP/i,                      mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-reface-yc",  label: "Yamaha Reface YC (2015, MIDI controller)",  nameMatch: /Reface[\s-]?YC/i,                      mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-genos",      label: "Yamaha Genos / Genos 2 (2017, MIDI controller)", nameMatch: /\bGenos\b/i,                       mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-psr-sx",     label: "Yamaha PSR-SX (2018→, MIDI controller)",    nameMatch: /PSR-?SX[0-9]*/i,                       mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-tyros5",     label: "Yamaha Tyros 5 (2013, MIDI controller)",    nameMatch: /Tyros[\s-]?5/i,                        mapping: baseYamahaKeyboard(0) },
  { id: "yamaha-p-series",   label: "Yamaha P-125/P-225/P-525 (2018→, MIDI keyboard)", nameMatch: /Yamaha[\s-]?P-?(1[25]5|22[05]|515|525)/i, mapping: baseYamahaKeyboard(0) },
  // Deck-B variants — pair a second Yamaha keyboard to control deck B
  { id: "yamaha-keyboard-b", label: "Yamaha keyboard → Deck B (second unit)",    nameMatch: /^__unmatchable__$/,                    mapping: baseYamahaKeyboard(1) },

  // Yamaha DTX-Multi 12 + successors (drum pad triggers)
  { id: "yamaha-dtx-multi12",label: "Yamaha DTX-Multi 12 (2011, pad triggers)",  nameMatch: /DTX[\s-]?Multi[\s-]?12/i,              mapping: baseYamahaDTXMulti() },
  { id: "yamaha-dtx-pro",    label: "Yamaha DTX-Pro / Pro X (2020, pad triggers)", nameMatch: /DTX[\s-]?Pro/i,                       mapping: baseYamahaDTXMulti() },

  // Yamaha digital mixers via USB-MIDI (channel strips → deck faders)
  { id: "yamaha-tf-series",  label: "Yamaha TF1/TF3/TF5/TF-Rack (2015, digital mixer)", nameMatch: /Yamaha[\s-]?TF[\s-]?(1|3|5|Rack)?/i, mapping: baseYamahaDigitalMixer() },
  { id: "yamaha-dm3",        label: "Yamaha DM3 (2023, digital mixer)",          nameMatch: /Yamaha[\s-]?DM3/i,                     mapping: baseYamahaDigitalMixer() },
  { id: "yamaha-dm7",        label: "Yamaha DM7 (2023, digital mixer)",          nameMatch: /Yamaha[\s-]?DM7/i,                     mapping: baseYamahaDigitalMixer() },
  { id: "yamaha-01v96i",     label: "Yamaha 01V96i (2011, digital mixer)",       nameMatch: /01V96[\s-]?i?/i,                       mapping: baseYamahaDigitalMixer() },
  { id: "yamaha-mgp-xu",     label: "Yamaha MG10XU/12XU/16XU/20XU (2014, USB mixer)", nameMatch: /Yamaha[\s-]?MG[\s-]?(10|12|16|20)XU/i, mapping: baseYamahaDigitalMixer() },
  // Broad Yamaha catch-all — anything Yamaha that isn't matched above and
  // reports as a MIDI device. Assumes keyboard-style controller (safest
  // default; a mixer that lands here will still get its channel-1 fader
  // wired to deck-A volume via CC 7).
  { id: "yamaha-any-keyboard", label: "Yamaha (generic keyboard fallback)",      nameMatch: /Yamaha/i,                              mapping: baseYamahaKeyboard(0) },
  // Broad catch-all: any port whose name contains "Pioneer" but doesn't match
  // a specific model above (macOS often surfaces gear as "USB MIDI Interface
  // (Pioneer)" or a plain model number like "DDJ 200"). Ships the classic
  // 2-deck DDJ mapping which covers Play/Cue/Sync/Load/Volume/EQ/Crossfader/
  // Tempo/Jog/Hotcues 1-4 across nearly every Pioneer controller since 2010.
  // If the auto-map is wrong for a specific model the user still runs Learn
  // — but for the 95% common case they get instant play/cue without leaving Studio.
  // Chinese clones of DDJ-SZ / SX / SR often self-report as "SZ Pro",
  // "SZ Clone", "Serato Controller", or bare "DJ Controller Pro" — none of
  // which match the pioneer/DDJ/XDJ/CDJ terms above. Their MIDI implementations
  // typically MIRROR the Pioneer originals to appear Serato-compatible, so
  // routing them through baseFourDeck gives immediate play/cue/sync/xfader
  // etc. Placed BEFORE the 2-deck fallback because more clones ship with
  // 4-deck hardware than 2-deck. Bind-last-signal pill still catches anything
  // truly bespoke.
  { id: "serato-clone-4deck", label: "Serato-style 4-deck clone (auto)", nameMatch: /\bSZ\b|\bSX\b|serato|4-?deck|four.?deck/i, mapping: baseFourDeck() },
  // Broadest possible Pioneer catch-all (2-deck floor). Keep LAST so specific
  // models above win; only unrecognised knock-offs land here.
  { id: "pioneer-any-2deck", label: "Pioneer DJ · generic 2-deck fallback", nameMatch: /pioneer|\bDDJ\b|\bXDJ\b|\bCDJ\b|djm|dj[-_ ]?controller/i, mapping: baseTwoDeck() },
];

/**
 * Given a MIDIInput port name, find the best matching preset. Returns null
 * if no match — the UI will offer MIDI Learn as the fallback.
 */
export function findPresetForDevice(portName) {
  if (!portName) return null;
  for (const p of PIONEER_PRESETS) {
    if (p.nameMatch.test(portName)) return p;
  }
  return null;
}

/** Human-readable list of every action we can dispatch. Used by the MIDI
 *  Learn UI's action-picker dropdown. */
export const ACTION_CATALOG = [
  { id: "play",         label: "Play / Pause" },
  { id: "cue",          label: "Cue" },
  { id: "sync",         label: "Sync (Beatmatch)" },
  { id: "load",         label: "Load selected track" },
  { id: "tempo",        label: "Tempo / Pitch fader" },
  { id: "volume",       label: "Channel fader" },
  { id: "crossfader",   label: "Crossfader (global)" },
  { id: "eq_low",       label: "EQ Low" },
  { id: "eq_mid",       label: "EQ Mid" },
  { id: "eq_high",      label: "EQ High" },
  { id: "jog",          label: "Jog wheel (nudge)" },
  { id: "hotcue_1",     label: "Hot Cue 1" },
  { id: "hotcue_2",     label: "Hot Cue 2" },
  { id: "hotcue_3",     label: "Hot Cue 3" },
  { id: "hotcue_4",     label: "Hot Cue 4" },
  { id: "hotcue_5",     label: "Hot Cue 5" },
  { id: "hotcue_6",     label: "Hot Cue 6" },
  { id: "hotcue_7",     label: "Hot Cue 7" },
  { id: "hotcue_8",     label: "Hot Cue 8" },
  { id: "loop_in",      label: "Loop In" },
  { id: "loop_out",     label: "Loop Out" },
  { id: "loop_half",    label: "Loop Halve" },
  { id: "loop_double",  label: "Loop Double" },
];
